import { now } from '@urpflanze/core/dist/cjs/Utilities';
import { mod } from '@urpflanze/core/dist/cjs/math';
import { Emitter } from './Emitter';
/**
 * Is used for sequence time management.
 * It is necessary to set the duration and the number of frames per second (frame rate).
 *
 * @category Timeline
 * @class Timeline
 * @extends {Emitter<ITimelineEvents>}
 */
class Timeline extends Emitter {
    constructor(duration = 60000, framerate = 30, tickMode = 'linear') {
        super();
        this.fps_samples_size = 30;
        this.fps_samples = [];
        this.fps_samples_index = 0;
        this.currentLoop = 0;
        this.paused_time = 0;
        this.sequence = {
            duration,
            framerate,
            frames: Math.round((duration / 1000) * framerate),
        };
        this.tick_time = 1000 / this.sequence.framerate;
        this.fps = this.sequence.framerate;
        this.b_sequence_started = false;
        this.currentFrame = 0;
        this.currentTime = 0;
        this.last_tick = 0;
        this.start_time = 0;
        this.tickMode = tickMode;
    }
    //#region sequence meta
    /**
     * Return the sequence
     *
     * @returns {Sequence}
     */
    getSequence() {
        return { ...this.sequence };
    }
    /**
     * Set Sequence
     *
     * @param {number} duration in ms
     * @param {number} framerate
     * @param {number} atTime
     */
    setSequence(duration, framerate, atTime) {
        this.sequence.duration = duration;
        this.sequence.framerate = framerate;
        this.tick_time = 1000 / this.sequence.framerate;
        this.sequence.frames = Math.round((this.sequence.duration / 1000) * this.sequence.framerate);
        if (typeof atTime !== 'undefined') {
            this.setTime(atTime);
        }
        else {
            this.dispatch('timeline:update_sequence', this.getSequence());
        }
    }
    /**
     * Set duration of timeline
     *
     * @param {number} framerate
     */
    setDuration(duration) {
        this.setSequence(duration, this.sequence.framerate);
    }
    /**
     * Get timeline duration
     *
     * @returns {number}
     */
    getDuration() {
        return this.sequence.duration;
    }
    /**
     * Return framerate
     *
     * @returns {number}
     */
    getFramerate() {
        return this.sequence.framerate;
    }
    /**
     * Set a framerate
     *
     * @param {number} framerate
     */
    setFramerate(framerate) {
        this.setSequence(this.sequence.duration, framerate);
    }
    /**
     * Get number of frames based on duration and framerate
     *
     * @returns {number}
     */
    getFramesCount() {
        return this.sequence.frames;
    }
    //#endregion meta
    //#region change status
    bSequenceStarted() {
        return this.b_sequence_started;
    }
    /**
     * Start the sequence
     *
     */
    start() {
        if (!this.b_sequence_started) {
            this.b_sequence_started = true;
            this.start_time = this.paused_time;
            this.dispatch('timeline:change_status', Timeline.START);
        }
    }
    /**
     * Pause the sequence
     *
     */
    pause() {
        if (this.b_sequence_started) {
            this.paused_time = now();
            this.b_sequence_started = false;
            this.dispatch('timeline:change_status', Timeline.PAUSE);
        }
    }
    /**
     * Stop the sequence and reset
     *
     */
    stop() {
        if (this.b_sequence_started) {
            this.b_sequence_started = false;
            this.currentTime = 0;
            this.currentFrame = 0;
            this.start_time = 0;
            this.paused_time = 0;
            this.dispatch('timeline:change_status', Timeline.STOP);
        }
    }
    /**
     * Animation tick
     *
     * @param {number} timestamp current timestamp
     * @returns {boolean}
     */
    tick(timestamp) {
        if (this.b_sequence_started) {
            if (!this.start_time) {
                this.start_time = timestamp;
                this.last_tick = -this.tick_time;
                this.currentFrame = -1;
            }
            const currentTime = timestamp - this.start_time;
            const elapsed = currentTime - this.last_tick;
            if (elapsed >= this.tick_time) {
                this.calculateFPS(1 / (elapsed / 1000));
                this.last_tick = currentTime;
                if (this.tickMode !== 'async') {
                    this.currentFrame = (this.currentFrame + 1) % this.sequence.frames;
                    this.currentTime = this.getFrameTime(this.currentFrame - 1);
                    this.currentLoop = currentTime === 0 ? 0 : Math.ceil(this.currentTime / this.sequence.duration) - 1;
                }
                else {
                    const currentTimeFixed = currentTime - (elapsed % this.tick_time);
                    this.currentLoop = currentTime === 0 ? 0 : Math.ceil(currentTimeFixed / this.sequence.duration) - 1;
                    this.currentTime = currentTimeFixed % this.sequence.duration;
                    this.currentFrame = this.getFrameAtTime(this.currentTime);
                }
                this.dispatch('timeline:progress', {
                    currentFrame: this.currentFrame,
                    currentTime: this.currentTime,
                    fps: this.fps,
                });
                return true;
            }
        }
        return false;
    }
    /**
     * Return sequence loop
     */
    getCurrentLoop() {
        return this.currentLoop;
    }
    /**
     * Calculate fps
     *
     * @private
     * @param {number} currentFPS
     */
    calculateFPS(currentFPS) {
        const samples = this.fps_samples.length;
        if (samples > 0) {
            let average = 0;
            for (let i = 0; i < samples; i++)
                average += this.fps_samples[i];
            this.fps = Math.round(average / samples);
        }
        this.fps_samples[this.fps_samples_index] = Math.round(currentFPS);
        this.fps_samples_index = (this.fps_samples_index + 1) % this.fps_samples_size;
    }
    //#endregion
    //#region Frame and Time
    /**
     * Return current animation frame
     *
     * @returns {number}
     */
    getCurrentFrame() {
        return this.currentFrame;
    }
    /**
     * get the time at specific frame number
     *
     * @param {number} frame
     * @returns {number}
     */
    getFrameTime(frame) {
        frame = mod(frame, this.sequence.frames);
        return (frame * this.tick_time) % this.sequence.duration;
    }
    /**
     * Return frame number at time
     *
     * @param {number} time
     * @returns {number}
     */
    getFrameAtTime(time) {
        return Math.round((time % this.sequence.duration) / this.tick_time);
    }
    /**
     * set current frame (0 to sequence.frames - 1)
     *
     * @param {number} frame
     */
    setFrame(frame) {
        this.currentFrame = mod(frame, this.sequence.frames);
        this.currentTime = this.getFrameTime(this.currentFrame);
        this.dispatch('timeline:progress', {
            currentFrame: this.currentFrame,
            currentTime: this.currentTime,
            fps: this.fps,
        });
    }
    /**
     * Return tick time (based on framerate)
     *
     * @returns {number}
     */
    getTickTime() {
        return this.tick_time;
    }
    /**
     * Return the current time
     *
     * @returns {number}
     */
    getTime() {
        return this.currentTime;
    }
    /**
     * Set animation at time
     *
     * @param {number} time
     */
    setTime(time) {
        time = mod(time, this.sequence.duration);
        this.currentTime = time;
        this.currentFrame = this.getFrameAtTime(time);
        this.dispatch('timeline:progress', {
            currentFrame: this.currentFrame,
            currentTime: this.currentTime,
            fps: this.fps,
        });
    }
}
/**
 * Animation status started
 * @internal
 */
Timeline.START = 'start';
/**
 * Animation status paused
 * @internal
 */
Timeline.PAUSE = 'pause';
/**
 * Animation status stop
 * @internal
 */
Timeline.STOP = 'stop';
export { Timeline };
//# sourceMappingURL=Timeline.js.map